import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export enum Stages {
  TestInstructions,
  InterviewSetup,
  Interview,
  Feedback,
  Continuation,
  Disqualification,
}

interface DsaChatResponse {
  hire_status: string
  response: string
  open_code_editor: boolean
  interview_finish: boolean
  new_session_created: boolean
  proctored: boolean
  error: string
  problem: {
    start_time: number
    end_time: number
  }
  session_id: string
  next_problem: boolean
  end_interview: boolean
}

interface Feedback {
  response: string
  hireStatus: string
}

interface DSAJudgeState {
  currentStage: Stages
  editorValue: string | null
  isUserSpeaking: boolean
  feedback: Feedback | null
  isSocketLoading: boolean
  response: string
  wordCurrentIndex: number
  isGeneratingResponse: boolean
  isSocketDisconnected: boolean
  botResponse: DsaChatResponse | null
  problemDescription: string
  hasInterviewEnded: boolean
}

const initialState: DSAJudgeState = {
  currentStage: Stages.TestInstructions,
  editorValue: null,
  feedback: null,
  isUserSpeaking: false,
  isSocketLoading: true,
  response: "",
  wordCurrentIndex: 0,
  isGeneratingResponse: false,
  isSocketDisconnected: false,
  botResponse: null,
  problemDescription: "",
  hasInterviewEnded: false,
}

const DSAJudgeSlice = createSlice({
  name: "dsa-judge",
  initialState,
  reducers: {
    setCurrentStage: (state, action: PayloadAction<Stages>) => {
      state.currentStage = action.payload
    },
    setEditorValue: (state, action: PayloadAction<string | null>) => {
      state.editorValue = action.payload
    },
    setFeedback: (state, action: PayloadAction<Feedback>) => {
      state.feedback = action.payload
    },
    setIsUserSpeaking: (state, action: PayloadAction<boolean>) => {
      state.isUserSpeaking = action.payload
    },
    setIsSocketLoading: (state, action) => {
      state.isSocketLoading = action.payload
    },
    appendResponseText: (state, action) => ({
      ...state,
      response: state.response + " " + action.payload,
    }),
    setClearResponse: (state) => ({
      ...state,
      response: "",
    }),
    setLanguageCurrentWordIndex: (state, action) => {
      state.wordCurrentIndex = action.payload
    },
    setIsGeneratingResponse: (state, action) => {
      state.isGeneratingResponse = action.payload
    },
    setIsSocketDisconnected: (state, action) => {
      state.isSocketDisconnected = action.payload
    },
    setBotResponse: (state, action) => {
      state.botResponse = action.payload
    },
    setProblemDescription: (state, action) => {
      state.problemDescription = action.payload
    },
    setHasInterviewEnded: (state, action) => {
      state.hasInterviewEnded = action.payload
    },
    resetState: () => initialState,
  },
})

export const {
  setCurrentStage,
  setEditorValue,
  setFeedback,
  setIsUserSpeaking,
  setBotResponse,
  setIsSocketLoading,
  appendResponseText,
  setClearResponse,
  setLanguageCurrentWordIndex,
  setIsGeneratingResponse,
  setIsSocketDisconnected,
  setProblemDescription,
  setHasInterviewEnded,
  resetState,
} = DSAJudgeSlice.actions
export default DSAJudgeSlice.reducer
